/**
 * Plan Wizard
 * Multi-step wizard for creating retirement plans
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, type NavigateFunction } from 'react-router-dom';
import { Sidebar } from '../Sidebar/Sidebar';
import type { SidebarSection, PlanPillsConfig } from '../Sidebar/Sidebar';
import { SidebarLayout } from '../Sidebar/SidebarLayout';
import { AlternativePlanBanner } from '../AlternativePlanBanner/AlternativePlanBanner';
import Step1PlanBasics from './Step1PlanBasics';
import Step2People from './Step2People';
import Step3Holdings from './Step3Holdings';
import Step1PlanBasicsMobile from './Step1PlanBasics.mobile';
import Step2PeopleMobile from './Step2People.mobile';
import Step3HoldingsMobile from './Step3Holdings.mobile';
import Step4PropertyMobile from './Step4Property.mobile';
import Step5SupplementaryIncomeMobile from './Step5SupplementaryIncome.mobile';
import Step5SpendingMobile from './Step5Spending.mobile';
import StepTwilightYearsMobile from './StepTwilightYears.mobile';
import Step6AssumptionsMobile from './Step6Assumptions.mobile';
import Step7StrategyMobile from './Step7Strategy.mobile';
import StepReviewMobile from './StepReview.mobile';
import { useIsMobile } from '../../hooks/useIsMobile';
import './WizardMobile.css';
import Step4Property from './Step4Property';
import Step5SupplementaryIncome from './Step5SupplementaryIncome';
import Step6Spending from './Step5Spending';
import Step7TwilightYears from './StepTwilightYears';
import Step8Assumptions from './Step6Assumptions';
import Step9Strategy from './Step7Strategy';
import StepReview from './StepReview';
import { WizardState, type AllStepFormData, type PropertyItem, type ContributionPersonData } from '../../types/wizard';
import * as planApi from '../../services/planApi';
import { computeStepChanges, getStepFields, FIELD_LABELS } from '../../utils/planDiff';
import { getPlanPair, savePlanPair, isPairValidForCopy, refreshPlanPairFromServer } from '../../services/planPairStore';
import type { PlanPair } from '../../types/alternativePlan';
import { trackWizardStepCompleted, trackProjectionGenerated } from '../../services/analytics';
import { stripLegacyFields } from './stripLegacyFields';
import { WIZARD_STEPS } from '../../constants/wizardSteps';
import { WizardProgressBar } from './WizardProgressBar';
import { WizardPreviewCard } from './WizardPreviewCard';
import { WizardWelcome } from './WizardWelcome';
import './PlanWizard.css';

/**
 * Everything the wizard has collected so far. Each step writes its own slice,
 * and the whole object is what gets saved and transformed for the API. Steps
 * receive all of it, not just their own fields — step 3 reads planType and
 * persons from steps 1 and 2.
 */
type WizardFormData = Partial<AllStepFormData>;

/** A payload assembled for the projection API, built by mutating a copy. */
type ApiPayload = Record<string, unknown>;

// Debounce helper with cancel support
function useDebounce<TArgs extends unknown[]>(callback: (...args: TArgs) => void, delay: number) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const debounced = useCallback(
    (...args: TArgs) => {
      cancel();
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        callbackRef.current(...args);
      }, delay);
    },
    [delay, cancel]
  );

  return { debounced, cancel };
}

/**
 * Transform wizard ihtStrategies into the API's ihtStrategyConfig format.
 * Maps charitableLegacyEnabled boolean → charitableLegacyPct number.
 */
function transformForApi(formData: WizardFormData): ApiPayload {
  // Deliberately widened: the function replaces wizard-shaped fields with
  // API-shaped ones (contributions, properties, ihtStrategyConfig), so the
  // result is no longer WizardFormData.
  const result: ApiPayload = { ...formData };

  if (formData.ihtStrategies) {
    const s = formData.ihtStrategies;
    const hasAnyStrategy = s.annualGiftExemption ||
      s.smallGiftRecipientsPerYear > 0 ||
      s.charitableLegacyEnabled ||
      s.normalExpenditureFromIncome ||
      s.annualPetAmount > 0 ||
      s.annualCharitableDonation > 0;

    if (hasAnyStrategy) {
      result.ihtStrategyConfig = {
        annualGiftExemption: s.annualGiftExemption,
        smallGiftRecipientsPerYear: s.smallGiftRecipientsPerYear,
        charitableLegacyPct: s.charitableLegacyEnabled ? 0.1 : 0,
        normalExpenditureFromIncome: s.normalExpenditureFromIncome,
        annualPetAmount: s.annualPetAmount,
        annualCharitableDonation: s.annualCharitableDonation,
      };
    }
    delete result.ihtStrategies;
  }

  // Transform contributions for API
  if (formData.contributions) {
    const contribEntries: Array<[string, ContributionPersonData]> = Object.entries(formData.contributions ?? {});
    const apiContributions = contribEntries
      .filter(([, c]) => c?.makingContributions)
      .map(([key, c]) => {
        const personIndex = Number.parseInt(key.replace('person', ''), 10);
        const monthlyGross = (c.monthlyPersonalContribution || 0) * 1.25; // Add basic rate relief
        return {
          personIndex,
          annualSippContributionGross: monthlyGross * 12,
          annualEmployerContribution: c.includeEmployer ? (c.monthlyEmployerContribution || 0) * 12 : 0,
          annualIsaContribution: c.annualIsaContribution || 0,
          contributionEndDate: c.contributionEndDate || '',
        };
      })
      .filter(c => c.annualSippContributionGross > 0 || c.annualEmployerContribution > 0 || c.annualIsaContribution > 0);

    // Replace wizard-format contributions with API-format, or remove if empty
    delete result.contributions;
    if (apiContributions.length > 0) {
      result.contributions = apiContributions;
    }
  }

  // Transform properties array for API: map wizard PropertyItem[] to API format
  if (formData.properties && Array.isArray(formData.properties) && formData.properties.length > 0) {
    result.properties = formData.properties.map((prop: PropertyItem) => ({
      propertyId: prop.propertyId,
      label: prop.label,
      propertyType: prop.propertyType,
      owner: prop.owner,
      currentMarketValue: prop.currentMarketValue,
      currentMortgageAmount: prop.currentMortgageAmount,
      currentEquity: prop.currentEquity,
      planningSale: prop.planningSale,
      plannedSaleDate: prop.plannedSaleDate,
      estateAgentFeePct: prop.estateAgentFeePct,
      newPropertyPurchasePrice: prop.newPropertyPurchasePrice,
      proceedsStrategy: prop.proceedsStrategy,
      monthlyMortgagePayment: prop.monthlyMortgagePayment,
      mortgageEndDate: prop.mortgageEndDate,
      purchasePrice: prop.purchasePrice ?? 0,
      acquisitionCosts: prop.acquisitionCosts ?? 0,
      improvementCosts: prop.improvementCosts ?? 0,
      interestOnlyMortgageAmount: prop.interestOnlyMortgageAmount ?? 0,
      mortgageInterestRatePct: prop.mortgageInterestRatePct ?? 0,
      legalFees: prop.legalFees ?? 0,
      rental: prop.rental ?? null,
    }));
  }

  return result;
}

const DECEASED_AGE_RE = /Deceased age (\d+) must be greater than retirement age (\d+)/;

function humanizeValidationMessage(msg: string): string {
  const match = DECEASED_AGE_RE.exec(msg);
  if (match) {
    return `Life expectancy (age ${match[1]}) must be later than retirement age (${match[2]}). Go back to Step 7 to adjust.`;
  }
  return msg;
}

// ---------- Draft-loading helpers (defined outside component to keep loadDraft complexity low) ----------

type DraftSummary = {
  planId: string;
  step: number;
  name: string;
  savedAt: string;
  createdAt: string;
  /** Server-derived pair role — rendered, never inferred (#1579 PR 0). */
  role: 'primary' | 'alternative';
};

/** Resolve start step from draft step, URL override, and from-projection flag. */
function resolveStartStep(
  draftStep: number,
  stepOverrideParam: string | null,
  fromProjection: boolean,
): number {
  const maxAllowed = fromProjection ? 10 : draftStep;
  const override = stepOverrideParam ? Number(stepOverrideParam) : null;
  return override && override >= 1 && override <= maxAllowed ? override : draftStep;
}

/** Resolve start step for alternative mode (caps at draftStep unless from-projection). */
function resolveAltStartStep(
  draftStep: number,
  stepOverrideParam: string | null,
  fromProjection: boolean,
): number {
  const altMaxAllowed = fromProjection ? 10 : draftStep;
  const override = stepOverrideParam ? Number(stepOverrideParam) : null;
  return override && override >= 1 && override <= altMaxAllowed
    ? override
    : draftStep >= 10 ? 10 : draftStep;
}

type LoadAltDraftResult = {
  startStep: number;
  maxStepReached: number;
  altPlan: Awaited<ReturnType<typeof planApi.getPlan>>;
};

/** Load the alternative-mode draft and primary snapshot. Returns the computed start step. */
async function loadAlternativeModeDraft(
  altPlanIdParam: string,
  primaryPlanIdParam: string | null,
  stepOverrideParam: string | null,
  fromProjection: boolean,
  setCounterpartSnapshot: (s: Record<string, unknown> | null) => void,
  setPlanPair: (p: PlanPair | null) => void,
): Promise<LoadAltDraftResult> {
  const [altPlan, primaryPlan] = await Promise.all([
    planApi.getPlan(altPlanIdParam),
    primaryPlanIdParam ? planApi.getPlan(primaryPlanIdParam) : Promise.resolve(null),
  ]);

  if (primaryPlan) {
    setCounterpartSnapshot(primaryPlan.formData);
  }

  // The SERVER's declared roles are the authority on the pair (#1617) — a
  // bookmarked or stale ?mode=alternative URL can claim a reversed pairing
  // (the #1576 bug class), so the URL's params are only a fallback for the
  // deploy window where roles are unavailable. The counterpart snapshot is
  // unaffected either way: it is "the other plan's data", whichever label
  // that plan carries.
  const serverPair = await refreshPlanPairFromServer().catch((err: unknown) => {
    console.warn('Plan pair refresh failed; falling back to URL-derived pair:', err);
    return null;
  });
  if (serverPair) {
    setPlanPair(serverPair);
  } else if (primaryPlan) {
    const pair: PlanPair = {
      primaryPlanId: primaryPlan.planId,
      primaryName: primaryPlan.name,
      alternativePlanId: altPlan.planId,
      alternativeName: altPlan.name,
    };
    savePlanPair(pair);
    setPlanPair(pair);
  } else {
    // No server roles and no primaryPlan param — fall back to the cached
    // pair only if it actually matches the alt plan we just loaded.
    const existingPair = getPlanPair();
    if (existingPair?.alternativePlanId === altPlan.planId) {
      setPlanPair(existingPair);
    }
  }

  const startStep = resolveAltStartStep(altPlan.step, stepOverrideParam, fromProjection);
  const maxStepReached = Math.max(altPlan.step, startStep, fromProjection ? 10 : 0);
  return { startStep, maxStepReached, altPlan };
}

type PlanPairDetectionCallbacks = {
  setPlanPair: (p: PlanPair | null) => void;
  setCounterpartSnapshot: (s: Record<string, unknown> | null) => void;
};

/** Fetch all of the user's plans if not already loaded. */
async function ensureAllDrafts(existing: DraftSummary[]): Promise<DraftSummary[]> {
  if (existing.length > 0) return existing;
  try {
    const { plans } = await planApi.getUserPlans();
    return plans ?? [];
  } catch (err) {
    console.error('Failed to fetch plans for pair detection:', err);
    return [];
  }
}

/**
 * Read which plan is primary from the server-declared role (#1579 PR 0).
 *
 * This function used to infer: sessionStorage pair first, then older
 * createdAt. That inference produced #1576 and #1577, and iOS would have
 * needed a third copy of it. The server now derives the role in one place
 * (server planListEnrichment.ts); the client renders what it is told.
 *
 * Returns null when the roles are missing or contradictory: a role-less
 * response is expected during a rolling deploy (old server, new client),
 * and contradictory roles would be a server bug. Either way the caller
 * skips pair detection for this load — the wizard stays fully usable on
 * the loaded draft, the pair UI is simply absent until the next load.
 * Degrading to no-pair makes NO claim about roles; inferring one (the
 * pre-PR-0 behaviour) is what produced #1576, and a hard failure would
 * lock users out of a wizard whose draft loaded fine.
 */
function resolvePairOrdering(
  twoPlans: DraftSummary[],
): { primary: DraftSummary; alt: DraftSummary } | null {
  if (twoPlans.length !== 2) {
    throw new Error(`resolvePairOrdering requires exactly 2 plans, received ${twoPlans.length}`);
  }
  const primary = twoPlans.find(p => p.role === 'primary');
  const alt = twoPlans.find(p => p.role === 'alternative');
  if (!primary || !alt) {
    console.warn(
      `Plan pair roles unavailable (roles: [${twoPlans.map(p => p.role ?? 'missing').join(', ')}]) — ` +
      'skipping pair detection for this load. Expected transiently during a deploy; ' +
      'persistent occurrences are a server bug.',
    );
    return null;
  }
  return { primary, alt };
}

/**
 * Detect a plan pair when the user has exactly two plans. Sets the pair
 * state and loads the counterpart's form data for diff badges.
 */
async function detectPlanPair(
  draft: Awaited<ReturnType<typeof planApi.getPlan>>,
  allDrafts: DraftSummary[],
  callbacks: PlanPairDetectionCallbacks,
): Promise<DraftSummary[]> {
  const resolvedDrafts = await ensureAllDrafts(allDrafts);
  if (resolvedDrafts.length !== 2) return resolvedDrafts;

  const ordering = resolvePairOrdering(resolvedDrafts);
  if (!ordering) return resolvedDrafts;
  const { primary, alt } = ordering;

  const pair: PlanPair = {
    primaryPlanId: primary.planId,
    primaryName: primary.name,
    alternativePlanId: alt.planId,
    alternativeName: alt.name,
  };
  savePlanPair(pair);
  callbacks.setPlanPair(pair);

  const counterpartId = draft.planId === primary.planId ? alt.planId : primary.planId;
  try {
    const counterpartDraft = await planApi.getPlan(counterpartId);
    callbacks.setCounterpartSnapshot(counterpartDraft.formData);
  } catch (err) {
    console.error('Failed to load counterpart plan for diff:', err);
  }

  return resolvedDrafts;
}

/**
 * Whether `planId` is the alternative plan, trusting the loaded plan's actual
 * identity (compared against the known pair) over the URL's `mode` param —
 * see the `isEditingAlternative` comment at its call site for why.
 */
function resolveIsAlternative(
  planId: string | undefined,
  pair: PlanPair | null,
  urlModeFallback: boolean,
): boolean {
  return pair ? planId === pair.alternativePlanId : urlModeFallback;
}

async function createFallbackProjection(
currentState: WizardState,
  altMode: boolean,
nav: NavigateFunction,
  setSaving: (v: boolean) => void,
  setError: (msg: string | null) => void,
): Promise<void> {
  try {
    setSaving(true);
    const apiPayload = transformForApi(currentState.formData);

    // Validate via the projection endpoint before navigating — surfaces schema
    // errors to the user instead of landing them on a broken projection page.
    await planApi.createProjection(apiPayload);

    // Save plan so we have a planId to navigate to
    const draftResponse = await planApi.savePlan({
      planId: currentState.planId,
      step: currentState.maxStepReached,
      formData: apiPayload,
    });

    if (altMode) {
      void nav('/projections/compare');
    } else {
      void nav(`/plans/${draftResponse.planId}/projection`);
    }
  } catch (error) {
    console.error('Failed to create projection:', error);
    const apiError = error as Error & { details?: string[] };
    if (apiError.details && apiError.details.length > 0) {
      setError(apiError.details.map(humanizeValidationMessage).join('. '));
    } else {
      setError(`Failed to create projection: ${humanizeValidationMessage(apiError.message || 'Unknown error')}. Please try again.`);
    }
  } finally {
    setSaving(false);
  }
}

type RedirectToProjectionParams = {
  draft: Awaited<ReturnType<typeof planApi.getPlan>>;
  isAlternativeMode: boolean;
  stepOverrideParam: string | null;
  fromProjection: boolean;
  navigate: NavigateFunction;
};

/**
 * If the user has a completed plan and is arriving fresh (no step override, not from projection
 * page, not in alternative mode), redirect them straight to the projection page.
 * Returns true if navigation was triggered (caller should skip further wizard state updates).
 */
async function tryRedirectToProjection({
  draft,
  isAlternativeMode,
  stepOverrideParam,
  fromProjection,
  navigate,
}: RedirectToProjectionParams): Promise<boolean> {
  if (isAlternativeMode || stepOverrideParam || fromProjection || draft.step < 10) {
    return false;
  }

  // Navigate directly to the plan's projection page — computed on demand.
  void navigate(`/plans/${draft.planId}/projection`, { replace: true });
  return true;
}

// ---------- handleReviewSubmit helpers ----------

interface StepRouterProps {
  step: number;
  isMobile: boolean;
  planId: string | undefined;
  formData: WizardFormData;
  lastSaved: string | undefined;
  onNext: (data: WizardFormData) => void;
  onSave: (data: WizardFormData) => void;
  onBack: () => void;
  onEditStep: (step: number) => void;
  onViewProjection: () => void;
  isSubmitting: boolean;
  isAlternative: boolean;
  projectionReady: boolean;
  autoProjectionError: string | null;
}

type StepRendererFn = (p: {
  key: string;
  formData: WizardFormData;
  ls: string | undefined;
  onNext: (data: WizardFormData) => void;
  onSave: (data: WizardFormData) => void;
  onBack: () => void;
  onEditStep: (step: number) => void;
  onViewProjection: () => void;
  isSubmitting: boolean;
  isAlternative: boolean;
  projectionReady: boolean;
  autoProjectionError: string | null;
}) => JSX.Element;

const MOBILE_STEP_RENDERERS: Record<number, StepRendererFn> = {
  1: (p) => <Step1PlanBasicsMobile key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} lastSaved={p.ls} />,
  2: (p) => <Step2PeopleMobile key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} onBack={p.onBack} lastSaved={p.ls} />,
  3: (p) => <Step3HoldingsMobile key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} onBack={p.onBack} lastSaved={p.ls} />,
  4: (p) => <Step4PropertyMobile key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} onBack={p.onBack} lastSaved={p.ls} />,
  5: (p) => <Step5SupplementaryIncomeMobile key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} onBack={p.onBack} lastSaved={p.ls} />,
  6: (p) => <Step5SpendingMobile key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} onBack={p.onBack} lastSaved={p.ls} />,
  7: (p) => <StepTwilightYearsMobile key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} onBack={p.onBack} lastSaved={p.ls} />,
  8: (p) => <Step6AssumptionsMobile key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} onBack={p.onBack} lastSaved={p.ls} />,
  9: (p) => <Step7StrategyMobile key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} onBack={p.onBack} lastSaved={p.ls} />,
  10: (p) => <StepReviewMobile key={p.key} formData={p.formData} onEditStep={p.onEditStep} onViewProjection={p.onViewProjection} onBack={p.onBack} isSubmitting={p.isSubmitting} isAlternative={p.isAlternative} projectionReady={p.projectionReady} autoProjectionError={p.autoProjectionError} />,
};

const DESKTOP_STEP_RENDERERS: Record<number, StepRendererFn> = {
  1: (p) => <Step1PlanBasics key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} lastSaved={p.ls} />,
  2: (p) => <Step2People key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} onBack={p.onBack} lastSaved={p.ls} />,
  3: (p) => <Step3Holdings key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} onBack={p.onBack} lastSaved={p.ls} />,
  4: (p) => <Step4Property key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} onBack={p.onBack} onGoToStep={p.onEditStep} lastSaved={p.ls} />,
  5: (p) => <Step5SupplementaryIncome key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} onBack={p.onBack} lastSaved={p.ls} />,
  6: (p) => <Step6Spending key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} onBack={p.onBack} lastSaved={p.ls} />,
  7: (p) => <Step7TwilightYears key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} onBack={p.onBack} onGoToStep={p.onEditStep} lastSaved={p.ls} />,
  8: (p) => <Step8Assumptions key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} onBack={p.onBack} lastSaved={p.ls} />,
  9: (p) => <Step9Strategy key={p.key} initialData={p.formData} onNext={p.onNext} onSave={p.onSave} onBack={p.onBack} lastSaved={p.ls} />,
  10: (p) => <StepReview key={p.key} formData={p.formData} onEditStep={p.onEditStep} onViewProjection={p.onViewProjection} onBack={p.onBack} isSubmitting={p.isSubmitting} isAlternative={p.isAlternative} projectionReady={p.projectionReady} autoProjectionError={p.autoProjectionError} />,
};

function StepRouter(props: Readonly<StepRouterProps>) {
  const { step, isMobile, planId, formData, lastSaved,
    onNext, onSave, onBack, onEditStep, onViewProjection,
    isSubmitting, isAlternative, projectionReady, autoProjectionError } = props;

  const renderers = isMobile ? MOBILE_STEP_RENDERERS : DESKTOP_STEP_RENDERERS;
  const renderFn = renderers[step];
  if (!renderFn) return null;

  return renderFn({
    key: planId ?? '',
    formData,
    ls: lastSaved ?? undefined,
    onNext, onSave, onBack, onEditStep, onViewProjection,
    isSubmitting, isAlternative, projectionReady, autoProjectionError,
  });
}

function PlanWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAlternativeMode = searchParams.get('mode') === 'alternative';
  const primaryPlanIdParam = searchParams.get('primaryPlan');
  const altPlanIdParam = searchParams.get('altPlan');
  const planIdParam = searchParams.get('plan');
  const stepOverrideParam = searchParams.get('step');
  const fromProjectionParam = searchParams.get('from') === 'projection';
  const isMobile = useIsMobile();

  const [wizardState, setWizardState] = useState<WizardState>({
    currentStep: 1,
    maxStepReached: 1,
    formData: {},
  });
  const [isSaving, setIsSaving] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [planPair, setPlanPair] = useState<PlanPair | null>(() => getPlanPair());
  // Track whether the auto-projection has completed at least once (for the Review step CTA).
  const [autoProjectionReady, setAutoProjectionReady] = useState(false);

  // Whether the wizard is actually displaying/editing the alternative plan.
  // `isAlternativeMode` reflects the URL's `mode` param, which encodes navigation
  // *intent* and is what the load effect below uses to decide how to fetch. But intent
  // and outcome can diverge (e.g. a caller navigates without setting `mode`, or a bad/
  // missing plan ID makes the loader fall back to a different plan). Once a plan pair
  // is known, trust the ID that actually got loaded — not the URL — for anything the
  // user sees (banners, labels, pill selection), so a load mismatch is at worst
  // mislabelled-then-corrected rather than silently misleading.
  const isEditingAlternative = resolveIsAlternative(wizardState.planId, planPair, isAlternativeMode);

  // Show welcome screen for new users (no existing draft).
  // Set to true after draft load completes with no planId.
  const [showWelcome, setShowWelcome] = useState(false);
  const welcomeDismissedRef = useRef(false);

  // Plan loading state — prevents flash of empty Step 1 while fetching saved plan.
  // Variable kept named `isDraftLoading` to minimise churn; the entity is now "plan"
  // everywhere else.
  const [isDraftLoading, setIsDraftLoading] = useState(true);
  // Surfaced when /plans/draft/:id fails (network, 5xx, decryption error). Without
  // this the wizard used to silently fall back to an empty form, which made users
  // think their saved plan had vanished and tempted them to overwrite it.
  const [draftLoadError, setDraftLoadError] = useState<string | null>(null);

  // Auto-projection error — surfaced on Review step so the user isn't stuck
  const [autoProjectionError, setAutoProjectionError] = useState<string | null>(null);

  // Inline error banner — replaces native alert() dialogs
  const [wizardError, setWizardError] = useState<string | null>(null);

  // Reset-all-to-primary dialog state
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  // Other plan's form data for diff comparison (primary in alt mode, alternative in normal mode)
  const [counterpartSnapshot, setCounterpartSnapshot] = useState<Record<string, unknown> | null>(null);

  // Ref to track latest state for use in async callbacks (avoids stale closures)
  const wizardStateRef = useRef(wizardState);
  wizardStateRef.current = wizardState;

  // Auto-projection dedup: skip if payload hash unchanged, cancel in-flight requests
  const lastProjectionHashRef = useRef<string | null>(null);
  const projectionAbortRef = useRef<AbortController | null>(null);

  // Fire-and-forget auto-projection from merged form data.
  // Deduplicates by payload hash, cancels in-flight requests.
  // When showErrors is false (mid-wizard save), errors are logged but not surfaced.
  const triggerAutoProjection = (mergedFormData: WizardFormData, showErrors = true) => {
    let apiPayload: Record<string, unknown>;
    try {
      apiPayload = transformForApi(mergedFormData);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to prepare projection data';
      console.error('transformForApi failed:', msg, err);
      if (showErrors) {
        setAutoProjectionError(msg);
      }
      return;
    }

    // Phase 1C: server no longer needs comparisonGroupId / isPrimary — pair
    // membership lives in sessionStorage on the client.

    // Dedup: skip projection if payload hasn't changed since last successful one
    const payloadHash = JSON.stringify(apiPayload);
    if (payloadHash === lastProjectionHashRef.current) return;

    // Cancel any in-flight projection request
    projectionAbortRef.current?.abort();
    const abortController = new AbortController();
    projectionAbortRef.current = abortController;

    void planApi.createProjection(apiPayload, abortController.signal).then((_projResponse) => {
      if (abortController.signal.aborted) return;
      lastProjectionHashRef.current = payloadHash;
      setAutoProjectionError(null);
      setAutoProjectionReady(true);
      trackProjectionGenerated(apiPayload.decumulationStrategy as string | undefined);
    }).catch((err) => {
      if (abortController.signal.aborted) return;
      const msg = err instanceof Error ? err.message : String(err);
      const details = (err as { details?: unknown })?.details;
      console.error('Auto-projection error:', msg, details ?? '');
      if (showErrors) {
        const displayMsg = Array.isArray(details) && details.length > 0
          ? details.map(humanizeValidationMessage).join('; ')
          : humanizeValidationMessage(msg);
        setAutoProjectionError(displayMsg);
      }
    });
  };

  // Auto-save with 1-second debounce
  // Uses ref to get latest state and avoid stale closure issues
  const saveToBackend = async (data: WizardFormData) => {
    const currentState = wizardStateRef.current;
    try {
      setIsSaving(true);
      setHasPendingChanges(false);
      const response = await planApi.savePlan({
        planId: currentState.planId,
        step: currentState.maxStepReached,
        formData: { ...currentState.formData, ...data },
      });

      setWizardState(prev => ({
        ...prev,
        planId: response.planId,
        lastSaved: response.savedAt,
      }));

      // Only auto-project when all steps are complete (step 10 = Review).
      // Mid-wizard saves have incomplete data — don't waste a server round-trip.
      // On Step 10 itself, surface errors so users aren't stuck on an infinite spinner;
      // on earlier steps keep auto-projection silent (showErrors = false).
      if (currentState.maxStepReached >= 10) {
        const mergedFormData = { ...currentState.formData, ...data };
        const onReviewStep = currentState.currentStep >= 10;
        triggerAutoProjection(mergedFormData, onReviewStep);
      }
    } catch (error) {
      console.error('Failed to save draft:', error);
      setWizardError('Your latest changes could not be saved. Please check your connection and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const { debounced: debouncedSave, cancel: cancelDebouncedSave } = useDebounce(saveToBackend, 1000);

  // Handle save (on blur or manual save)
  const handleSave = useCallback(
    (data: WizardFormData) => {
      setWizardState(prev => {
        const merged = stripLegacyFields({ ...prev.formData, ...data });
        return { ...prev, formData: merged };
      });
      setHasPendingChanges(true);  // Show "Saving..." immediately
      debouncedSave(data);
    },
    [debouncedSave]
  );

  // Handle back button
  const handleBack = useCallback(() => {
    setWizardState(prev => ({
      ...prev,
      currentStep: Math.max(1, prev.currentStep - 1),
    }));
  }, []);

  // Handle step navigation (breadcrumb click)
  const handleStepClick = useCallback(
    async (stepNumber: number) => {
      // Use ref to get latest state — avoids stale closure when handleSave
      // has updated formData but the useCallback hasn't re-created yet
      const currentState = wizardStateRef.current;

      // Only allow navigation to steps user has already reached (not current step, not future steps)
      if (stepNumber > currentState.maxStepReached || stepNumber === currentState.currentStep) {
        return;
      }

      // Save current step data before navigating
      // Use maxStepReached to preserve progress when navigating back to earlier steps
      try {
        setIsSaving(true);
        await planApi.savePlan({
          planId: currentState.planId,
          step: currentState.maxStepReached,
          formData: currentState.formData,
        });
      } catch (error) {
        console.error('Failed to save before navigation:', error);
        // Continue with navigation even if save fails
      } finally {
        setIsSaving(false);
      }

      // Navigate to selected step
      setWizardState(prev => ({
        ...prev,
        currentStep: stepNumber,
      }));
    },
    [] // No dependencies — uses ref for latest state
  );

  // Handle next step
  const handleNext = useCallback(
    async (data: WizardFormData) => {
      setWizardError(null);
      // Use ref to get latest state (avoids stale closure issues)
      const currentState = wizardStateRef.current;

      // Review step has its own handler (handleReviewSubmit)
      if (currentState.currentStep >= 10) return;

      // Update state
      const updatedFormData = { ...currentState.formData, ...data };
      setWizardState(prev => ({
        ...prev,
        formData: updatedFormData,
      }));

      // All steps before Review use the standard save-and-advance flow
      {
        // If returning from an edit-from-review, jump back to Review (step 10)
        const nextStep = currentState.returnToReview ? 10 : currentState.currentStep + 1;
        try {
          setIsSaving(true);
          const response = await planApi.savePlan({
            planId: currentState.planId,
            step: nextStep,
            formData: { ...currentState.formData, ...data },
          });

          setWizardState(prev => ({
            ...prev,
            currentStep: nextStep,
            maxStepReached: Math.max(prev.maxStepReached, nextStep),
            formData: { ...prev.formData, ...data },
            planId: response.planId,
            lastSaved: response.savedAt,
            returnToReview: false,
          }));

          trackWizardStepCompleted(currentState.currentStep, currentState.formData.planType);

          // Trigger auto-projection when advancing to Review (step 10).
          // handleNext saves the draft directly (not via debouncedSave),
          // so the auto-projection in saveToBackend never fires.
          if (nextStep === 10) {
            triggerAutoProjection({ ...currentState.formData, ...data });
          }
        } catch (error: unknown) {
          console.error('handleNext ERROR:', error);
          // Continue navigation even if save fails — the form data is already in
          // local state, and the debounced auto-save will retry. Don't show an
          // alarming error banner since we're advancing the step (a "Failed to save"
          // alert paired with forward navigation is a UX contradiction).
          setWizardState(prev => ({
            ...prev,
            currentStep: nextStep,
            maxStepReached: Math.max(prev.maxStepReached, nextStep),
            formData: { ...prev.formData, ...data },
            returnToReview: false,
          }));
          trackWizardStepCompleted(currentState.currentStep, currentState.formData.planType);
          if (nextStep === 10) {
            triggerAutoProjection({ ...currentState.formData, ...data });
          }
        } finally {
          setIsSaving(false);
        }
      }
    },
    [] // Remove dependencies - we use ref to get latest state
  );

  // Handle Review step submission (Step 10 → navigate to projection)
  const handleReviewSubmit = useCallback(() => {
    setWizardError(null);
    void (async () => {
      // Re-derive from the actually-loaded plan (not the URL's `mode` param) so this
      // matches wherever the draft really ended up, even if navigation intent and the
      // loaded plan diverged.
      const planId = wizardStateRef.current.planId;
      const pair = getPlanPair();
      const isAlt = resolveIsAlternative(planId, pair, isAlternativeMode);

      // Alternative mode: navigate to compare page (projections computed on demand)
      if (isAlt && pair?.primaryPlanId && pair?.alternativePlanId) {
        void navigate('/projections/compare');
        return;
      }

      // Primary mode: navigate directly to this draft's projection page
      if (!isAlt && planId) {
        void navigate(`/plans/${planId}/projection`);
        return;
      }

      // Fallback: no draft ID yet — create a projection directly
      await createFallbackProjection(
        wizardStateRef.current,
        isAlt,
        navigate,
        setIsSaving,
        setWizardError,
      );
    })();
  }, [navigate, isAlternativeMode]);

  // Handle "Edit" from Review step — jump back to a specific step, return on Next
  const handleEditStep = useCallback((stepNumber: number) => {
    // Clear stale error and dedup hash so the Review button
    // shows "Generating..." until the re-projection completes after editing.
    setAutoProjectionError(null);
    lastProjectionHashRef.current = null;
    setAutoProjectionReady(false);
    setWizardState(prev => ({
      ...prev,
      currentStep: stepNumber,
      returnToReview: true,
    }));
  }, []);

  // Load draft on mount or when URL changes (plan switch)
  // Cancel any pending debounced save so it doesn't write stale data to the new plan's draft
  useEffect(() => {
    cancelDebouncedSave();
    let cancelled = false;

    const loadDraft = async () => {
      setIsDraftLoading(true);
      // Clear stale counterpart snapshot immediately on plan switch
      setCounterpartSnapshot(null);

      try {
        const fromProjection = fromProjectionParam;

        // Alternative mode: load alt draft for editing, primary as snapshot
        if (isAlternativeMode && altPlanIdParam) {
          const { startStep, maxStepReached, altPlan } = await loadAlternativeModeDraft(
            altPlanIdParam,
            primaryPlanIdParam,
            stepOverrideParam,
            fromProjection,
            setCounterpartSnapshot,
            setPlanPair,
          );
          setWizardState({
            currentStep: startStep,
            maxStepReached,
            formData: altPlan.formData,
            planId: altPlan.planId,
            lastSaved: altPlan.savedAt,
          });
          return;
        }

        // Normal mode: resolve draft ID from URL param or most recent draft
        let planId = planIdParam;
        let allDrafts: DraftSummary[] = [];

        if (!planId) {
          const { plans } = await planApi.getUserPlans();
          allDrafts = plans ?? [];
          if (allDrafts.length > 0) {
            planId = allDrafts[0].planId;
          }
        }

        if (!planId) {
          // No existing draft — show welcome for first-time users
          if (!isAlternativeMode && !welcomeDismissedRef.current) {
            setShowWelcome(true);
          }
          return;
        }

        // Load the draft
        const draft = await planApi.getPlan(planId);

        // Detect plan pair when user has exactly two plans (updates allDrafts if needed)
        const resolvedDrafts = await detectPlanPair(draft, allDrafts, {
          setPlanPair,
          setCounterpartSnapshot,
        });

        // Compute start step from URL override and from-projection flag
        const startStep = resolveStartStep(draft.step, stepOverrideParam, fromProjection);

        // Redirect returning users to projection. When a pair exists, always
        // redirect to the PRIMARY plan — the loaded draft may be the
        // alternative if it was the most recently updated.
        let redirectDraft = draft;
        const redirectOrdering = resolvedDrafts.length === 2 ? resolvePairOrdering(resolvedDrafts) : null;
        if (redirectOrdering) {
          const { primary } = redirectOrdering;
          if (draft.planId !== primary.planId) {
            try {
              redirectDraft = await planApi.getPlan(primary.planId);
            } catch {
              // Primary plan load failed — keep the loaded draft as fallback.
            }
          }
        }

        const redirected = await tryRedirectToProjection({
          draft: redirectDraft,
          isAlternativeMode,
          stepOverrideParam,
          fromProjection,
          navigate,
        });
        if (redirected) {
          // Keep loading spinner visible during navigation
          cancelled = true;
          return;
        }

        setWizardState({
          currentStep: startStep,
          maxStepReached: Math.max(draft.step, startStep, fromProjection ? 10 : 0),
          formData: draft.formData,
          planId: draft.planId,
          lastSaved: draft.savedAt,
        });
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load draft:', error);
          const message = error instanceof Error ? error.message : 'Unknown error';
          setDraftLoadError(`Couldn't load your saved plan: ${message}. Refresh to try again.`);
        }
      } finally {
        if (!cancelled) setIsDraftLoading(false);
      }
    };

    void loadDraft();
    return () => { cancelled = true; };
  }, [isAlternativeMode, altPlanIdParam, primaryPlanIdParam, planIdParam, stepOverrideParam, fromProjectionParam, cancelDebouncedSave, navigate]);

  // When a returning user loads a plan that already has maxStepReached >= 10, mark
  // autoProjectionReady immediately so the Review step button is enabled while the
  // background re-projection runs.  The auto-projection still fires (via the effect below)
  // and will replace the stale result once complete.
  const draftLoadedRef = useRef(false);
  useEffect(() => {
    if (wizardState.planId && !draftLoadedRef.current) {
      draftLoadedRef.current = true;
      if (wizardState.maxStepReached >= 10) {
        setAutoProjectionReady(true);
      }
    }
  }, [wizardState.planId, wizardState.maxStepReached]);

  // Auto-projection when landing directly on Step 10 (e.g. returning to a completed plan).
  // The advance-to-step-10 trigger in handleNext only fires during step navigation,
  // so direct loads at step 10 need this fallback.
  const step10TriggeredRef = useRef(false);
  useEffect(() => {
    if (wizardState.currentStep !== 10) {
      step10TriggeredRef.current = false;
      return;
    }
    if (!wizardState.planId) return; // Plan not loaded yet
    if (step10TriggeredRef.current) return; // Already handled this load
    step10TriggeredRef.current = true;

    // Always re-project on Step 10 load to ensure projection matches current draft settings.
    // A stale sessionStorage projection ID from a previous session could show results
    // computed with different settings (e.g., fixed allocation vs time-horizon rebalancing).
    const { formData } = wizardStateRef.current;
    if (Object.keys(formData).length > 0) {
      const id = setTimeout(() => triggerAutoProjection(formData), 0);
      return () => clearTimeout(id);
    }
  }, [wizardState.currentStep, wizardState.planId]);

  const [mobileOpen, setMobileOpen] = useState(false);


  // Compute step changes for sidebar badges (works in both modes when pair exists)
  const stepChanges = useMemo(() => {
    if (!counterpartSnapshot) return null;
    return computeStepChanges(counterpartSnapshot as Record<string, unknown>, wizardState.formData as Record<string, unknown>);
  }, [counterpartSnapshot, wizardState.formData]);

  // Count how many steps have changes from primary (for reset dialog)
  const changedStepCount = useMemo(() =>
    stepChanges?.filter(sc => sc.hasChanges).length ?? 0, [stepChanges]);

  // Check if current step has changes from counterpart
  const currentStepHasChanges = useMemo(() => {
    if (!counterpartSnapshot || !stepChanges) return false;
    return stepChanges.find(sc => sc.stepNumber === wizardState.currentStep)?.hasChanges ?? false;
  }, [counterpartSnapshot, stepChanges, wizardState.currentStep]);

  // Changed field labels for the current step (used in diff strip)
  const currentStepChangedFields = useMemo(() => {
    if (!stepChanges) return [];
    const sc = stepChanges.find(s => s.stepNumber === wizardState.currentStep);
    if (!sc || !sc.hasChanges) return [];
    return sc.changedFields.map(f => FIELD_LABELS[f] || f);
  }, [stepChanges, wizardState.currentStep]);

  // Reset current step's fields to primary values
  const handleResetToPrimary = useCallback(() => {
    if (!counterpartSnapshot) return;
    const fields = getStepFields(wizardState.currentStep);
    if (fields.length === 0) return;

    const resetData: Record<string, unknown> = {};
    for (const field of fields) {
      resetData[field] = counterpartSnapshot[field];
    }

    setWizardState(prev => ({
      ...prev,
      formData: { ...prev.formData, ...resetData },
    }));

    // Trigger auto-save of the reset data
    debouncedSave(resetData);
  }, [counterpartSnapshot, wizardState.currentStep, debouncedSave]);

  // Reset ALL steps of alternative to match primary
  const handleResetAllToPrimary = useCallback(async () => {
    if (!wizardState.planId) return;
    try {
      setIsResetting(true);
      setResetError(null);
      cancelDebouncedSave();
      const pair = getPlanPair();
      if (!isPairValidForCopy(pair, wizardState.planId)) {
        throw new Error("This page hasn't updated since you made changes elsewhere — reload the page and try again.");
      }
      await planApi.resetAlternativeToPrimary(wizardState.planId, pair.primaryPlanId);

      // Clear dedup hash so auto-projection re-runs with fresh data
      lastProjectionHashRef.current = null;

      setShowResetDialog(false);

      // Navigate to the primary plan so the user sees the correct state
      void navigate(`/plan?plan=${pair.primaryPlanId}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err) || 'Failed to reset';
      setResetError(msg);
    } finally {
      setIsResetting(false);
    }
  }, [wizardState.planId, cancelDebouncedSave, navigate]);

  const wizardPlanPills: PlanPillsConfig | undefined = useMemo(() => {
    if (!planPair) return undefined;
    const activePlan: 'primary' | 'alternative' = isEditingAlternative ? 'alternative' : 'primary';
    return {
      activePlan,
      hasAlternative: true,
      onSelectPlan: (plan: 'primary' | 'alternative') => {
        const currentStep = wizardStateRef.current.currentStep;
        if (plan === 'primary' && activePlan !== 'primary') {
          void navigate(`/plan?plan=${planPair.primaryPlanId}&step=${currentStep}`);
        } else if (plan === 'alternative' && activePlan !== 'alternative') {
          void navigate(`/plan?mode=alternative&primaryPlan=${planPair.primaryPlanId}&altPlan=${planPair.alternativePlanId}&step=${currentStep}`);
        }
      },
      onCompare: () => {
        void navigate('/projections/compare');
      },
      // In alternative mode, only enable Compare once the alternative has been projected
      // (maxStepReached >= 10) — prevents navigating to Compare before it's projectable.
      // In primary mode we cannot know the alternative's step without a fetch, so allow
      // it when both IDs exist (the Compare page will surface errors if not ready).
      canCompare: !!(planPair.primaryPlanId && planPair.alternativePlanId) &&
        (!isEditingAlternative || wizardState.maxStepReached >= 10),
      onResetToPrimary: isEditingAlternative ? () => setShowResetDialog(true) : undefined,
    };
  }, [planPair, isEditingAlternative, wizardState.maxStepReached, navigate]);

  const sidebarSections: SidebarSection[] = useMemo(() => [
    {
      id: 'plan-details',
      label: 'Plan Details',
      status: 'active' as const,
      collapsible: true,
      defaultCollapsed: false,
      items: WIZARD_STEPS.map(step => {
        let status: 'active' | 'completed' | 'disabled';
        if (wizardState.currentStep === step.number) {
          status = 'active';
        } else if (step.number <= wizardState.maxStepReached) {
          status = 'completed';
        } else {
          status = 'disabled';
        }
        // Add amber badge if step has changes from primary
        const hasChanges = stepChanges?.find(sc => sc.stepNumber === step.number)?.hasChanges ?? false;
        return {
          id: `step-${step.number}`,
          label: step.label,
          number: step.number,
          status,
          badge: hasChanges ? 'changed' as const : undefined,
        };
      }),
    },
    {
      id: 'projection',
      label: 'Projection',
      status: undefined, // Always clickable — navigates to projection or Step 10
      items: [],
    },
  ], [wizardState.currentStep, wizardState.maxStepReached, stepChanges]);

  const handleSidebarClick = useCallback((itemId: string) => {
    if (itemId === 'projection') {
      const planId = wizardStateRef.current.planId;
      if (planId && wizardStateRef.current.maxStepReached >= 10) {
        void navigate(`/plans/${planId}/projection`);
      } else {
        // No completed plan yet — go to Review step to generate one
        void handleStepClick(10);
      }
      return;
    }
    if (itemId === 'plan' || itemId === 'plan-details') return; // Already on plan
    const stepNumber = Number(itemId.replace('step-', ''));
    if (Number.isNaN(stepNumber) || stepNumber < 1 || stepNumber > 10) {
      console.error(`Invalid sidebar item ID: "${itemId}". Could not extract step number.`);
      return;
    }
    void handleStepClick(stepNumber);
  }, [handleStepClick, navigate]);

  // Wrapper callbacks to match 1-param step component signatures
  const stepOnNext = useCallback((data: WizardFormData) => {
    void handleNext(data);
  }, [handleNext]);
  const stepOnSave = useCallback((data: WizardFormData) => {
    void handleSave(data);
  }, [handleSave]);

  const wizardBody = (
    <div className="wizard-container">
      {/* Loading state — prevents flash of empty Step 1 while fetching saved plan */}
      {isDraftLoading ? (
        <div className="wizard-loading" aria-live="polite" aria-busy="true">
          <div className="app-loading-spinner" />
          <p className="wizard-loading-text">Loading your plan...</p>
        </div>
      ) : <>
      {draftLoadError && (
        <div className="wizard-load-error" role="alert">
          <p><strong>{draftLoadError}</strong></p>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => globalThis.location.reload()}>
            Refresh
          </button>
          <p className="help-text">Your data is safe. If the problem continues, contact support.</p>
        </div>
      )}
      {/* Welcome screen for first-time users */}
      {showWelcome && (
        <WizardWelcome onStart={() => {
          welcomeDismissedRef.current = true;
          setShowWelcome(false);
        }} />
      )}

      {/* Progress bar — shown during all wizard steps on desktop; mobile has its own step indicator in WizardMobileShell */}
      {!showWelcome && wizardState.currentStep <= 10 && !isMobile && (
        <WizardProgressBar currentStep={wizardState.currentStep} />
      )}

      {/* Preview card — early aha moment showing retirement age and pot size */}
      {!showWelcome && (
        <WizardPreviewCard formData={wizardState.formData} currentStep={wizardState.currentStep} />
      )}

      {/* Alternative Plan Banner */}
      {!showWelcome && isEditingAlternative && (
        <AlternativePlanBanner
          variant="editing"
          actions={
            <>
              {currentStepHasChanges && wizardState.currentStep < 10 && (
                <button
                  type="button"
                  className="wizard-alt-reset-btn"
                  onClick={handleResetToPrimary}
                >
                  Reset This Step
                </button>
              )}
              {changedStepCount > 0 && (
                <button
                  type="button"
                  className="wizard-alt-reset-all-btn"
                  onClick={() => setShowResetDialog(true)}
                >
                  Reset Alternative to My Plan
                </button>
              )}
            </>
          }
        />
      )}

      {/* Step diff strip — show which fields differ from the other plan */}
      {!showWelcome && currentStepChangedFields.length > 0 && wizardState.currentStep < 10 && (
        <div className="wizard-diff-strip" data-testid="wizard-diff-strip">
          <span className="wizard-diff-strip-dot" aria-hidden="true" />
          <span className="wizard-diff-strip-text">
            {isEditingAlternative ? 'Differs from My Plan' : 'Differs from Alternative'}:
            {' '}{currentStepChangedFields.join(', ')}
          </span>
        </div>
      )}

      {/* Inline error banner — replaces native alert() dialogs */}
      {wizardError && (
        <div className="wizard-error-banner" role="alert">
          <span className="wizard-error-banner-text">{wizardError}</span>
          <button
            type="button"
            className="wizard-error-banner-dismiss"
            onClick={() => setWizardError(null)}
            aria-label="Dismiss error"
          >
            &times;
          </button>
        </div>
      )}

      {/* Current Step — key={key} forces remount when switching plans,
          ensuring useForm re-initialises with the new draft's defaultValues */}
      {!showWelcome && (
        <StepRouter
          step={wizardState.currentStep}
          isMobile={isMobile}
          planId={wizardState.planId}
          formData={wizardState.formData}
          lastSaved={wizardState.lastSaved}
          onNext={stepOnNext}
          onSave={stepOnSave}
          onBack={handleBack}
          onEditStep={handleEditStep}
          onViewProjection={handleReviewSubmit}
          isSubmitting={isSaving}
          isAlternative={isEditingAlternative}
          projectionReady={autoProjectionReady}
          autoProjectionError={autoProjectionError}
        />
      )}


      {/* Reset All Confirmation Dialog */}
      {showResetDialog && (
        <div className="wizard-reset-backdrop" role="button" tabIndex={-1} onClick={() => !isResetting && setShowResetDialog(false)} onKeyDown={e => e.key === 'Escape' && !isResetting && setShowResetDialog(false)}>
          <div className="wizard-reset-dialog" role="dialog" aria-modal="true" aria-labelledby="reset-dialog-title" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
            <h3 id="reset-dialog-title">Reset Alternative to My Plan?</h3>
            <p>This will overwrite all changes in your Alternative plan with the current data from My Plan.</p>
            {changedStepCount > 0 && stepChanges && (
              <p className="wizard-reset-loss">
                {stepChanges
                  .filter(sc => sc.hasChanges)
                  .map(sc => WIZARD_STEPS.find(ws => ws.number === sc.stepNumber)?.label ?? `Step ${sc.stepNumber}`)
                  .join(', ')
                } will be overwritten.
              </p>
            )}
            {resetError && (
              <p className="wizard-reset-error">{resetError}</p>
            )}
            <div className="wizard-reset-actions">
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setShowResetDialog(false)}
                disabled={isResetting}
                autoFocus
              >
                Keep my changes
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleResetAllToPrimary}
                disabled={isResetting}
              >
                {isResetting ? (
                  <><span className="spinner" /> Resetting...</>
                ) : (
                  'Overwrite Alternative'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Saving Indicator */}
      {(hasPendingChanges || isSaving || wizardState.lastSaved) && (() => {
        const indicatorClassName = isSaving || hasPendingChanges ? 'saving' : 'saved';
        let indicatorContent;
        if (isSaving || hasPendingChanges) {
          indicatorContent = (
            <>
              <span className="spinner"></span>
              {' '}Saving...
            </>
          );
        } else if (wizardState.lastSaved) {
          indicatorContent = (
            <>
              <span className="checkmark">✓</span>{' '}
              <span>Saved — encrypted and secure</span>
            </>
          );
        } else {
          indicatorContent = null;
        }

        return (
          <div className={`saving-indicator ${indicatorClassName}`}>
            {indicatorContent}
          </div>
        );
      })()}
    </>}
    </div>
  );

  if (isMobile) return wizardBody;

  return (
    <SidebarLayout
      sidebar={
        <Sidebar
          sections={sidebarSections}
          activeItemId={isDraftLoading ? '' : `step-${wizardState.currentStep}`}
          onItemClick={handleSidebarClick}
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
          planPills={wizardPlanPills}
        />
      }
      mobileOpen={mobileOpen}
      onToggleMobile={() => setMobileOpen(prev => !prev)}
    >
      {wizardBody}
    </SidebarLayout>
  );
}

export default PlanWizard;
